import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { View } from 'react-native';

import { Text } from '@/components/ui';
import { getUserTextColor } from '@/utils/user-colors';

export interface PersonChipProps {
	/** User id — drives the deterministic colour when no colour is stored. */
	id?: string | null;
	name?: string | null;
	/** The persisted `users.color`, when the record carries one. */
	color?: string | null;
	size?: number;
	/** Falls back to this when the name is missing (a soft-deleted user, say). */
	fallback?: string;
	testID?: string;
}

/**
 * A person, rendered the same way everywhere: icon plus name, both in that person's
 * colour.
 *
 * This pair was written five times across the inbox and improvement surfaces — and each
 * copy called `getUserTextColor` twice, once for the icon and once for the label, when
 * that function runs a contrast search. Colour is how a manager is recognised at a
 * glance, so the two must never diverge; one component is the only way to guarantee it.
 *
 * Renders nothing without a name, so callers can pass optional fields straight through
 * instead of repeating the same conditional.
 */
export const PersonChip: React.FC<PersonChipProps> = ({
	id,
	name,
	color,
	size = 13,
	fallback,
	testID,
}) => {
	const label = name ?? fallback;
	if (!label) return null;
	const tone = getUserTextColor(id ?? '', color);
	return (
		<View
			testID={testID}
			// flexShrink 1 + minWidth 0, not flexShrink 0: an unshrinkable box gives
			// numberOfLines nothing to clip against, so on RN-Web a long name overflows
			// its column instead of ellipsising.
			style={{ flexDirection: 'row', alignItems: 'center', gap: 3, flexShrink: 1, minWidth: 0 }}
		>
			<Ionicons name="person-circle" size={size} color={tone} />
			<Text numberOfLines={1} style={{ fontSize: size - 1, fontWeight: '600', color: tone }}>
				{label}
			</Text>
		</View>
	);
};

export default PersonChip;
